import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { X, Save } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { edgeTunnelApi } from '@/lib/api'
import { toast } from 'react-hot-toast'

interface NodeEditModalProps {
  node: any
  groups: any[]
  onClose: () => void
  onSuccess: () => void
}

export default function NodeEditModal({ node, groups, onClose, onSuccess }: NodeEditModalProps) {
  const [formData, setFormData] = useState({
    name: node.name || '',
    location: node.location || '',
    ip_address: node.ip_address || '',
    port: node.port || 443,
    user_level: node.user_level || 1,
    status: node.status || 1,
    max_connections: node.max_connections || 100,
  })

  const updateMutation = useMutation({
    mutationFn: (data: any) => edgeTunnelApi.updateNode(node.id.toString(), data),
    onSuccess: () => {
      toast.success('节点更新成功')
      onSuccess()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '更新失败')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.ip_address.trim()) {
      toast.error('IP地址不能为空')
      return
    }

    updateMutation.mutate(formData)
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const group = groups.find(g => g.id === node.group_id)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">编辑节点</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-sm text-gray-500">
            服务组: {group?.name || '未知'}
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 节点名称 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                节点名称
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="可选，留空则显示IP地址"
              />
            </div>

            {/* 位置 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                位置
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="如：香港、新加坡等"
              />
            </div>

            {/* IP地址 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IP地址 *
              </label>
              <input
                type="text"
                value={formData.ip_address}
                onChange={(e) => handleInputChange('ip_address', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="192.168.1.1"
                required
              />
            </div>

            {/* 端口 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                端口
              </label>
              <input
                type="number"
                value={formData.port}
                onChange={(e) => handleInputChange('port', parseInt(e.target.value) || 443)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1"
                max="65535"
              />
            </div>

            {/* 用户等级 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                用户等级
              </label>
              <select
                value={formData.user_level}
                onChange={(e) => handleInputChange('user_level', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={1}>所有用户</option>
                <option value={2}>标准用户</option>
                <option value={3}>VIP专享</option>
              </select>
            </div>

            {/* 最大连接数 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                最大连接数
              </label>
              <input
                type="number"
                value={formData.max_connections}
                onChange={(e) => handleInputChange('max_connections', parseInt(e.target.value) || 100)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1"
                max="1000"
              />
            </div>

            {/* 状态 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                状态
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="status"
                    value={1}
                    checked={formData.status === 1}
                    onChange={(e) => handleInputChange('status', parseInt(e.target.value))}
                    className="text-blue-600"
                  />
                  <Badge variant="default">启用</Badge>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="status"
                    value={0}
                    checked={formData.status === 0}
                    onChange={(e) => handleInputChange('status', parseInt(e.target.value))}
                    className="text-blue-600"
                  />
                  <Badge variant="secondary">禁用</Badge>
                </label>
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                取消
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending}
                className="flex-1 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {updateMutation.isPending ? '保存中...' : '保存'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}